<template>
    <MobileMenuToggle />
    <MegaMenuToggle v-if="isMegaMenu" />
    <Teleport :to="teleportSite" :disabled="!isMegaMenu">
        <nav class="primary-navigation"
             :class="{'mega-menu': (isMegaMenu && !mobileMenuActive), 'inline': (!isMegaMenu && !mobileMenuActive)}"
             ref="primaryNavigation">
            <ul class="navigation-menu">
                <li v-for="(item, index) in hierarchicalMenuItems"
                    class="nav-link"
                    :class="getItemClasses(item)">
                    <MenuLink :item="item"
                              :depth="1"
                              :isFirstTopLevelItem="index === 0"
                              :isLastTopLevelItem="index === hierarchicalMenuItems.length - 1"
                              :index="index"
                              :displayIcon="true" />
                    <Teleport :to="teleportSite"
                              :disabled="(!getItemClasses(item, 'array').includes('mega-sub-menu'))  || isMegaMenu || mobileMenuActive">
                        <div v-if="item.children.length > 0"
                             :id="'top-level-sub-menu-' + (index + 1)"
                             class="sub-menu-wrapper"
                             :class="{mega: (getItemClasses(item, 'array').includes('mega-sub-menu') && !isMegaMenu)}"
                             v-on:mouseleave="handleSubMenuMouseLeave(getItemClasses(item, 'array').includes('mega-sub-menu'))">
                            <ul class="sub-menu">
                                <SubMenuBackButton :item="item" />
                                <li class="root-menu-item">
                                    <MenuLink :item="item"
                                              :depth="2"
                                              :preventDefault="false"
                                              :isRootMenuItem="true"
                                              :preText="subMenuRootItemPreText"
                                              :postText="subMenuRootItemPostText" />
                                </li>
                                <li v-for="(child, index) in item.children"
                                    class="sub-menu-nav-link"
                                    :class="getItemClasses(child)">
                                    <MenuLink :item="child"
                                              :depth="2"
                                              :isLastChildItem="index === item.children.length - 1" />
                                    <div v-if="child.children.length > 0"
                                         class="sub-menu-wrapper">
                                        <ul class="sub-menu">
                                            <SubMenuBackButton :item="child" />
                                            <li class="root-menu-item">
                                                <MenuLink :item="child"
                                                          :depth="3"
                                                          :preventDefault="false"
                                                          :isRootMenuItem="true"
                                                          :preText="subMenuRootItemPreText"
                                                          :postText="subMenuRootItemPostText" />
                                            </li>
                                            <li v-for="(grandchild, index) in child.children"
                                                class="sub-menu-nav-link"
                                                :class="getItemClasses(grandchild)">
                                                <MenuLink :item="grandchild"
                                                          :depth="3"
                                                          :isLastChildItem="index === child.children.length - 1" />
                                                <div v-if="grandchild.children.length > 0"
                                                     class="sub-menu-wrapper">
                                                    <ul class="sub-menu">
                                                        <SubMenuBackButton :item="grandchild" />
                                                        <li class="root-menu-item">
                                                            <MenuLink :item="grandchild"
                                                                      :depth="4"
                                                                      :preventDefault="false"
                                                                      :isRootMenuItem="true"
                                                                      :preText="subMenuRootItemPreText"
                                                                      :postText="subMenuRootItemPostText" />
                                                        </li>
                                                        <li v-for="(greatGrandchild, index) in grandchild.children"
                                                            class="sub-menu-nav-link"
                                                            :class="getItemClasses(greatGrandchild)">
                                                            <MenuLink :item="greatGrandchild"
                                                                      :depth="4"
                                                                      :isLastChildItem="index === grandchild.children.length - 1" />
                                                        </li>
                                                    </ul>
                                                </div>
                                            </li>
                                        </ul>
                                    </div>
                                </li>
                            </ul>
                        </div>
                    </Teleport>
                </li>
            </ul>
        </nav>
    </Teleport>
</template>

<script>

import MobileMenuToggle from "./MobileMenuToggle.vue";
import MegaMenuToggle from "./MegaMenuToggle.vue";
import SubMenuBackButton from "./SubMenuBackButton.vue";
import MenuLink from "./MenuLink.vue";

export default {
    components: {MobileMenuToggle, MegaMenuToggle, SubMenuBackButton, MenuLink},
    data() {
        return {
            body:                        document.body,
            firstInteractiveElement:     null,
            activeSubMenuParentMenuLink: null,
            mobileMenuActive:            false,
            activeMegaSubMenu:           null,
            activeMegaMenuParentLink:    null,
        };
    },
    props: {
        menuItems:               {
            type:    Array,
            default: []
        },
        isMegaMenu:              {
            type:    Boolean,
            default: false
        },
        teleportSite:            {
            type:    String,
            default: "#primary-menu-teleport-site"
        },
        subMenuRootItemPreText:  {
            type:    String,
            default: ""
        },
        subMenuRootItemPostText: {
            type:    String,
            default: ""
        },
    },

    computed: {
        hierarchicalMenuItems() {
            // Create a copy of menuItems to avoid mutating props directly
            let menuItems = JSON.parse(JSON.stringify(this.menuItems));

            for (let i = menuItems.length - 1; i > 0; i--) {
                if(!menuItems[i].classes.includes('mobile-only')) {
                    menuItems[i].isLastItem = true;
                    break;
                }
            }


            let itemMap = {};
            let rootItems = [];

            // First pass: create a map of all items by ID and initialize children arrays
            menuItems.forEach(item => {
                item.children = [];
                itemMap[item.ID] = item;
            });

            // Second pass: build the hierarchy
            menuItems.forEach(item => {
                if (item.menu_item_parent && item.menu_item_parent !== "0") {
                    // This is a child item
                    const parentItem = itemMap[item.menu_item_parent];
                    if (parentItem) {
                        parentItem.children.push(item);
                    }
                } else {
                    // This is a root level item
                    rootItems.push(item);
                }
            });

            return rootItems;
        }
    },

    mounted() {
        console.log('Menu.vue mounted');
        // console.log(this.menuItems);
        console.log(this.hierarchicalMenuItems);

        if (window.matchMedia) {
            let mq = window.matchMedia(`(min-width: ${window.fc_data.mobile_nav_breakpoint})`);
            mq.addEventListener("change", () => {
                this.mqChangeHandler(mq);
            });
            this.mqChangeHandler(mq);
        }

        this.body.addEventListener('touchend', (event) => {
            this.handleBodyInteraction(event);
        });
        this.body.addEventListener('keydown', (event) => {
            this.handleBodyInteraction(event);
        });


        /**
         * If the user is tabbing backwards and they reach the last tabbable item before the header,
         * set focus to the last top-level menu-item if on desktop, or the hamburger menu if on mobile
         */
        setTimeout(() => {
            let siteHeader = document.querySelector('.site-header');
            let mainContent = document.getElementsByTagName('main')[0];
            let siteFooter = document.querySelector('.site-footer');

            let siteHeaderInteractiveElements = siteHeader ? siteHeader.querySelectorAll('input, button, select, textarea, a[href], span[tabindex="0"]') : [];
            let mainContentInteractiveElements = mainContent ? mainContent.querySelectorAll('input, button, select, textarea, a[href], span[tabindex="0"]') : [];
            let siteFooterInteractiveElements = siteFooter ? document.querySelector('.site-footer').querySelectorAll('input, button, select, textarea, a[href], span[tabindex="0"]') : [];


            const primaryNavItems = this.$refs.primaryNavigation.querySelectorAll('a'); // Get currently focused element
            const lastPrimaryNavItem = primaryNavItems[primaryNavItems.length - 1 ]; // Get currently focused element
            const nextTabbableHeaderElement = this.getNextTabbableElement(siteHeader.querySelector('.container'), lastPrimaryNavItem);

            if (nextTabbableHeaderElement) {
                this.firstInteractiveElement = nextTabbableHeaderElement;
            } else if (mainContentInteractiveElements.length) {
                this.firstInteractiveElement = mainContentInteractiveElements[0];
            } else if (siteFooterInteractiveElements.length) {
                this.firstInteractiveElement = siteFooterInteractiveElements[0];
            } else {
                this.firstInteractiveElement = siteHeaderInteractiveElements[0];
            }
            // console.log(this.firstInteractiveElement);
            this.firstInteractiveElement.addEventListener('keydown', (e) => {
                if (e.key === 'Tab' && e.shiftKey) {
                    // console.log("first interactive element inside <main> tabbed over");
                    setTimeout(() => {
                        if (this.mobileMenuActive) {
                            // console.log('setting menu-toggle to focus')
                            document.querySelector('.menu-toggle-wrapper.mobile .menu-toggle').focus();
                        } else {
                            if (this.isMegaMenu) {
                                document.querySelector('.menu-toggle-wrapper.mega .menu-toggle').focus();
                            } else {
                                // console.log('setting last top-level menu-item to focus')
                                let topLevelMenuLinks = this.$refs.primaryNavigation.querySelectorAll('.navigation-menu > li.nav-link > .menu-link');
                                topLevelMenuLinks[topLevelMenuLinks.length - 1].focus();
                            }

                        }
                    }, 20);
                }
            });

        }, 100);

    },
    methods: {
        getNextTabbableElement(container, currentElement) {
            const tabbableElements = container.querySelectorAll('a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])');
            const arrayTabbable = Array.from(tabbableElements);
            const currentIndex = arrayTabbable.indexOf(currentElement);

            if (currentIndex < 0 || currentIndex === arrayTabbable.length - 1) {
                return false;
            } else {
                return arrayTabbable[currentIndex + 1];
            }
        },
        getItemClasses(item, returnType = 'string') {
            if (item.children.length > 0) {
                item.classes.push('more');
            }
            if (returnType === 'array') {
                return item.classes;
            }
            return item.classes.join(' ');
        },
        mqChangeHandler(mq) {
            if (mq.matches) {
                this.mobileMenuActive = false;
                this.body.classList.remove('mobile-menu-open');

                if (this.isMegaMenu) {
                    let topLevelSubMenus = this.$refs.primaryNavigation.querySelectorAll('.sub-menu-wrapper');
                    let subMenuContainerHeight = 0;

                    let itemsProcessed = 0;
                    topLevelSubMenus.forEach((subMenu, index, array) => {
                        // console.log("scrollHeight: " + subMenu.scrollHeight);
                        if (subMenu.scrollHeight > subMenuContainerHeight) {
                            subMenuContainerHeight = subMenu.scrollHeight;
                        }

                        itemsProcessed++;
                        if (itemsProcessed === array.length) {
                            let topLevelMenuItemsContainer = this.$refs.primaryNavigation.querySelector('.navigation-menu');
                            topLevelMenuItemsContainer.style.height = subMenuContainerHeight + 'px';
                        }
                    });
                }
            } else {
                this.mobileMenuActive = true;
                this.body.classList.remove('mega-menu-open');
                if (this.$refs.primaryNavigation.querySelector('.expanded')) {
                    this.$refs.primaryNavigation.querySelector('.expanded').classList.remove('expanded');
                }
                // close and mega-sub-menus open on desktop when transitioning to mobile.
                let activeMegaSubMenu = document.querySelector('.mega.sub-menu-wrapper.active');
                if (activeMegaSubMenu) {
                    activeMegaSubMenu.classList.remove('active');
                }
            }
        },
        closeMegaMenu() {
            // console.log('closeMegaMenu')
            this.activeMegaMenuParentLink = null;
            this.$refs.primaryNavigation.querySelector('.active').classList.remove('active');
            this.body.classList.remove('mega-menu-open');
        },
        closeMobileMenu() {
            this.body.classList.remove('mobile-menu-open');
            if (this.$refs.primaryNavigation.querySelector('li.more.expanded')) {
                this.$refs.primaryNavigation.querySelector('li.more.expanded').classList.remove('expanded');
            }
            this.activeSubMenuParentMenuLink = null;
        },
        setFocusToNextItemOutsideMenu() {
            console.log('setting focus to next item outside menu')
            setTimeout(() => {
                this.firstInteractiveElement.focus();
            }, 20);
        },
        handleBodyInteraction(e) {
            // State: Mobile Nav
            if (this.mobileMenuActive) {
                // if the user preses "Escape", close the menu and set focus to the menu-toggle
                if (e.type === "keydown" && e.key === 'Escape') {
                    if (this.body.classList.contains('mobile-menu-open')) {
                        document.querySelector('.menu-toggle-wrapper.mobile .menu-toggle').focus();
                        this.closeMobileMenu();
                    }
                }
            }
            // State: Desktop
            else {
                if (this.isMegaMenu) {

                    if (e.type === "touchend" || e.type === "click") {
                        if (!e.target.closest('.mega-menu') && !e.target.closest('.menu-toggle-wrapper')) {
                            if (this.body.classList.contains('mega-menu-open')) {
                                this.closeMegaMenu();
                            }
                        }
                    } else if (e.type === "keydown" && e.key === 'Escape') {
                        if (this.body.classList.contains('mega-menu-open')) {
                            document.querySelector('.menu-toggle-wrapper.mega .menu-toggle').focus();
                            this.closeMegaMenu();
                        }
                    }
                } else {
                    if (e.type === "touchend") {
                        if (this.activeSubMenuParentMenuLink) {
                            if (!e.target.closest('.expanded')) {
                                this.activeSubMenuParentMenuLink.parentNode.classList.remove('expanded');
                                this.activeSubMenuParentMenuLink = null;
                            }
                        }
                        if (this.activeMegaSubMenu) {
                            if (!e.target.closest('.mega.sub-menu-wrapper.active')) {
                                this.activeMegaSubMenu.classList.remove('active');
                            }
                        }
                    }
                }
            }
        },
        handleSubMenuMouseLeave(isMegaSubMenu) {
            // console.log('handleSubMenuMouseLeave')
            if (isMegaSubMenu) {
                if (this.activeMegaSubMenu) {
                    this.activeMegaSubMenu.classList.remove('active');
                }
            }
        }
    }
}

</script>
